import { Box, Button, Stack, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AutoAwesome } from '@mui/icons-material';
import {
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import ModelParameters from '~/components/ModelSelection/ModelParameters';
import ModelSearch from '~/components/ModelSelection/ModelSearch';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { Modality } from '~/domain/dicom/modality';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { transformModelRoiNames } from '~/utils/transforms/modelRoiTransforms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import StructureSelection from './StructureSelect';

type AutoSegmentationModelSelectionProps = {
    modelCustomizations?: UserCustomization<ContouringCustomizationFile>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    onSubmit: (
        model: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
};

export default function AutoSegmentationModelSelection({
    availableModels = [],
    modelCustomizations = [],
    modality,
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: AutoSegmentationModelSelectionProps) {
    const [modelSearch, setModelSearch] = useState('');
    const [modelModality, setModelModality] = useState(
        modality ? [modality] : []
    );
    const [selectedBodyPart, setSelectedBodyPart] = useState('All');
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();
    const [selectedStructures, setSelectedStructures] = useState<string[]>([]);
    const [availableRoiNames, setAvailableRoiNames] = useState<string[]>([]);

    const filteredModels = useMemo(
        () =>
            availableModels?.filter((model) => {
                const matchesBodyPart =
                    selectedBodyPart === 'All' ||
                    model.bodyPart === selectedBodyPart;

                const matchesSearch = model.label
                    .toLowerCase()
                    .includes(modelSearch.toLowerCase());
                const matchesModality =
                    modelModality.length === 0 ||
                    model.inputModalities.some((tag) =>
                        modelModality.includes(tag as Modality)
                    );
                return matchesBodyPart && matchesSearch && matchesModality;
            }),
        [availableModels, selectedBodyPart, modelSearch, modelModality]
    );

    const onSelectedModelChanged = useCallback(
        (model: PredictionModel) => {
            setSelectedModel(model);
            setSelectedCustomization(undefined);

            const { roiNames, includedRoiNames } = transformModelRoiNames(
                modelCustomizations,
                model.name,
                undefined
            );

            setAvailableRoiNames(roiNames);

            // set default ROI selection
            setSelectedStructures(includedRoiNames);
        },
        [modelCustomizations]
    );

    const onSelectedCustomizationChanged = useCallback(
        (customizationName?: string) => {
            if (selectedModel) {
                setSelectedCustomization(customizationName);

                const { roiNames, includedRoiNames } = transformModelRoiNames(
                    modelCustomizations,
                    selectedModel.name,
                    customizationName
                );

                setAvailableRoiNames(roiNames);

                // set default ROI selection
                setSelectedStructures(includedRoiNames);
            }
        },
        [modelCustomizations, selectedModel]
    );

    useEffect(() => {
        if (modality) {
            setModelModality([modality]);
        }
    }, [modality]);

    const customizations = useMemo(() => {
        if (!selectedModel || !modelCustomizations) {
            return [];
        }
        const selectedModelCustomizations =
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            );
        return selectedModelCustomizations || [];
    }, [selectedModel, modelCustomizations]);

    const hasCustomizations = customizations.length > 0;

    return (
        <Box
            display="grid"
            gridTemplateRows="auto 240px auto"
            gap={2}
            sx={{ transition: '0.6s' }}
        >
            <Stack spacing={0.5}>
                <ModelSearch
                    availableModels={availableModels}
                    modelSearch={modelSearch}
                    setModelSearch={setModelSearch}
                    selectedBodyPart={selectedBodyPart}
                    setSelectedBodyPart={setSelectedBodyPart}
                    modelModality={modelModality}
                    setModelModality={setModelModality}
                />
            </Stack>
            <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns={
                    hasCustomizations ? '50% 1fr 1fr' : '75% 1fr'
                }
                gap={1}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={filteredModels}
                    selectedModel={selectedModel}
                    onSelectedModelChanged={onSelectedModelChanged}
                />

                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        onSelectedCustomizationChanged={
                            onSelectedCustomizationChanged
                        }
                    />
                )}

                <StructureSelection
                    selectedModel={selectedModel}
                    selectedStructures={selectedStructures}
                    roiNames={availableRoiNames}
                    setSelectedStructures={setSelectedStructures}
                    workflowType={WorkflowTypes.CONTOUR}
                />
            </Box>
            <Box>
                <Box
                    mt={0.5}
                    display="flex"
                    justifyContent="space-between"
                    gap={0.5}
                >
                    <ModelParameters
                        selectedModel={selectedModel}
                        selectedCustomization={selectedCustomization}
                        selectedStructures={selectedStructures}
                        roiNames={availableRoiNames}
                    />
                    <Stack gap={0.5}>
                        {showPseudonymization && <PseudonymizeCheckbox />}
                        <Tooltip
                            title={
                                !selectedModel ? (
                                    <FormattedMessage id="selectAModelToStart" />
                                ) : (
                                    ''
                                )
                            }
                            placement="top"
                        >
                            {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                            <Box component="span">
                                <Button
                                    onClick={() =>
                                        selectedModel &&
                                        selectedStructures?.length &&
                                        onSubmit(
                                            selectedModel,
                                            selectedStructures,
                                            selectedCustomization
                                        )
                                    }
                                    variant="contained"
                                    color="primary"
                                    endIcon={<AutoAwesome />}
                                    disabled={
                                        !selectedModel ||
                                        selectedStructures?.length === 0 ||
                                        disabled ||
                                        isSubmitting
                                    }
                                    sx={{ width: 160 }}
                                    data-testid="start-contour-auto-segmentation-button"
                                >
                                    <FormattedMessage id="start" />
                                </Button>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
